import { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { Card, Loading } from "@/components/ui-bits";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";
import { Plus, X, Trash2, Pencil, Mail, Phone, MapPin, Building, Users, Loader2, Search, KeyRound, FileText, Wrench, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const LINK_META = {
  property: { Icon: Building, cls: "bg-blue-50 text-blue-700 hover:bg-blue-600 hover:text-white" },
  tenancy: { Icon: KeyRound, cls: "bg-emerald-50 text-emerald-700 hover:bg-emerald-600 hover:text-white" },
  referencing: { Icon: FileText, cls: "bg-amber-50 text-amber-700 hover:bg-amber-600 hover:text-white" },
  workorders: { Icon: Wrench, cls: "bg-violet-50 text-violet-700 hover:bg-violet-600 hover:text-white" },
};

const TYPES = [
  { id: "landlord", label: "Landlord", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  { id: "tenant", label: "Tenant", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  { id: "guarantor", label: "Guarantor", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  { id: "contractor", label: "Contractor", cls: "bg-violet-50 text-violet-700 border-violet-200" },
];
const typeMeta = (t) => TYPES.find((x) => x.id === t) || { label: t, cls: "bg-slate-100 text-slate-600 border-slate-200" };

const SORTS = [
  { id: "first", label: "First name (A–Z)" },
  { id: "surname", label: "Surname (A–Z)" },
  { id: "newest", label: "Newest first" },
  { id: "oldest", label: "Oldest first" },
  { id: "popular", label: "Most active" },
];

const empty = { type: "landlord", first_name: "", surname: "", email: "", phone: "", address: "", company: "", notes: "" };

export default function Contacts() {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState(null);
  const [count, setCount] = useState(0);
  const [sort, setSort] = useState("newest");
  const [filter, setFilter] = useState("all");
  const [searchParams] = useSearchParams();
  const [q, setQ] = useState(searchParams.get("q") || "");
  useEffect(() => { setQ(searchParams.get("q") || ""); }, [searchParams]);
  const [modal, setModal] = useState(null); // { edit?, form }
  const [saving, setSaving] = useState(false);

  const load = () => {
    setContacts(null);
    return api.get(`/contacts?sort=${sort}`).then(({ data }) => { setContacts(data.contacts); setCount(data.count); });
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [sort]);

  const counts = useMemo(() => {
    const c = { all: contacts?.length || 0 };
    (contacts || []).forEach((x) => { c[x.type] = (c[x.type] || 0) + 1; });
    return c;
  }, [contacts]);

  const filtered = useMemo(() => {
    let list = contacts || [];
    if (filter !== "all") list = list.filter((c) => c.type === filter);
    const s = q.trim().toLowerCase();
    if (s.length >= 1) list = list.filter((c) => [c.name, c.email, c.phone, c.company, c.address].some((f) => (f || "").toLowerCase().includes(s)));
    return list;
  }, [contacts, filter, q]);

  const save = async (e) => {
    e.preventDefault();
    const f = modal.form;
    if (!f.first_name.trim() && !f.surname.trim()) { toast.error("Enter a first name or surname"); return; }
    setSaving(true);
    try {
      if (modal.edit) await api.patch(`/contacts/${modal.edit}`, f);
      else await api.post("/contacts", f);
      toast.success(modal.edit ? "Contact updated" : "Contact added");
      setModal(null);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not save contact"); }
    finally { setSaving(false); }
  };

  const del = async (c) => {
    let msg = `Delete contact "${c.name || "this contact"}"?`;
    if (c.type === "landlord" && c.property_count > 0) {
      msg = `Delete landlord "${c.name}"?\n\nThis will ARCHIVE their ${c.property_count} propert${c.property_count === 1 ? "y" : "ies"} and remove their portal account. Archived properties are kept for 7 years and can be restored from the Properties → Archived tab.\n\nContinue?`;
    } else if (c.type === "landlord") {
      msg = `Delete landlord "${c.name}"? This will also remove their portal account.`;
    }
    if (!window.confirm(msg)) return;
    try {
      const { data } = await api.delete(`/contacts/${c.contact_id}`);
      toast.success(data.message || "Contact deleted");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not delete contact"); }
  };

  return (
    <Layout>
      <div data-testid="contacts-page" className="flex flex-wrap items-end justify-between gap-4 mb-8 fade-up">
        <div>
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-blue-700 mb-2">Directory</p>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Contacts</h1>
          <p className="text-slate-500 mt-1 text-sm" data-testid="contacts-count">
            <b className="text-slate-800">{count}</b> {count === 1 ? "contact" : "contacts"} saved
          </p>
        </div>
        <button data-testid="add-contact-button" onClick={() => setModal({ form: { ...empty } })}
          className="bg-blue-700 text-white hover:bg-blue-800 shadow-sm rounded-lg px-4 py-2.5 text-sm font-medium transition-colors flex items-center gap-2">
          <Plus size={16} /> Add Contact
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-6 fade-up">
        <div className="flex items-center gap-1 flex-wrap">
          {[{ id: "all", label: "All" }, ...TYPES].map((t) => (
            <button key={t.id} onClick={() => setFilter(t.id)} data-testid={`contacts-filter-${t.id}`}
              className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${filter === t.id ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
              {t.label} <span className={filter === t.id ? "opacity-70" : "text-slate-400"}>{counts[t.id] || 0}</span>
            </button>
          ))}
        </div>
        <div className="ml-auto flex items-center gap-2">
          <div className="relative">
            <Search size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input data-testid="contacts-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search contacts…"
              className="pl-8 pr-3 py-2 text-sm rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-600 w-52" />
          </div>
          <select data-testid="contacts-sort" value={sort} onChange={(e) => setSort(e.target.value)}
            className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600">
            {SORTS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
        </div>
      </div>

      {!contacts ? <Loading /> : filtered.length === 0 ? (
        <div data-testid="no-contacts" className="text-center py-20 text-slate-400">
          <Users size={28} className="mx-auto mb-2 text-slate-300" />
          <p className="text-sm">{q || filter !== "all" ? "No contacts match your filter." : "No contacts yet — add one to get started."}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((c) => {
            const m = typeMeta(c.type);
            return (
              <Card key={c.contact_id} data-testid={`contact-row-${c.contact_id}`} className="p-5 fade-up group">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <h3 className="font-semibold text-slate-900 truncate">{c.name || "—"}</h3>
                    <span className={`inline-block mt-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border ${m.cls}`}>{m.label}</span>
                    {c.type === "landlord" && c.property_count > 0 && (
                      <span className="ml-2 text-[11px] text-slate-500">{c.property_count} propert{c.property_count === 1 ? "y" : "ies"}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-1 md:opacity-0 md:group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
                    <button data-testid={`edit-contact-${c.contact_id}`} onClick={() => setModal({ edit: c.contact_id, form: { type: c.type, first_name: c.first_name || "", surname: c.surname || "", email: c.email || "", phone: c.phone || "", address: c.address || "", company: c.company || "", notes: c.notes || "" } })}
                      title="Edit" className="w-7 h-7 rounded-lg hover:bg-slate-100 text-slate-500 flex items-center justify-center"><Pencil size={14} /></button>
                    <button data-testid={`delete-contact-${c.contact_id}`} onClick={() => del(c)}
                      title="Delete" className="w-7 h-7 rounded-lg hover:bg-red-50 text-red-500 flex items-center justify-center"><Trash2 size={14} /></button>
                  </div>
                </div>
                <div className="mt-3 space-y-1.5 text-sm text-slate-600">
                  {c.email && <p className="flex items-center gap-2 truncate"><Mail size={14} className="text-slate-400 shrink-0" /> <a href={`mailto:${c.email}`} className="hover:text-blue-700 truncate">{c.email}</a></p>}
                  {c.phone && <p className="flex items-center gap-2"><Phone size={14} className="text-slate-400 shrink-0" /> {c.phone}</p>}
                  {c.company && <p className="flex items-center gap-2 truncate"><Building size={14} className="text-slate-400 shrink-0" /> {c.company}</p>}
                  {c.address && <p className="flex items-center gap-2"><MapPin size={14} className="text-slate-400 shrink-0" /> <span className="truncate">{c.address}</span></p>}
                </div>
                {c.notes && <p className="mt-2 text-xs text-slate-500 bg-slate-50 rounded-md px-2 py-1.5 line-clamp-2">{c.notes}</p>}
                {c.links?.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-slate-100 flex flex-wrap gap-1.5">
                    {c.links.map((l, i) => {
                      const lm = LINK_META[l.kind] || LINK_META.property;
                      return (
                        <button key={i} data-testid={`contact-link-${c.contact_id}-${i}`} onClick={() => navigate(l.to)}
                          title={`Go to ${l.label}`}
                          className={`inline-flex items-center gap-1 rounded-full text-xs font-medium px-2.5 py-1 transition-colors ${lm.cls}`}>
                          <lm.Icon size={12} /> <span className="truncate max-w-[160px]">{l.label}</span> <ArrowRight size={11} />
                        </button>
                      );
                    })}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm" onClick={() => setModal(null)}>
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-display text-xl font-bold text-slate-900">{modal.edit ? "Edit Contact" : "Add Contact"}</h3>
              <button onClick={() => setModal(null)} className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
            </div>
            <form onSubmit={save} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Type</label>
                <select data-testid="contact-type" value={modal.form.type} onChange={(e) => setModal((m) => ({ ...m, form: { ...m.form, type: e.target.value } }))}
                  className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600">
                  {TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <TField label="First name" testid="contact-first" value={modal.form.first_name} onChange={(v) => setModal((m) => ({ ...m, form: { ...m.form, first_name: v } }))} placeholder="Jane" />
                <TField label="Surname" testid="contact-surname" value={modal.form.surname} onChange={(v) => setModal((m) => ({ ...m, form: { ...m.form, surname: v } }))} placeholder="Smith" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <TField label="Email" testid="contact-email" type="email" value={modal.form.email} onChange={(v) => setModal((m) => ({ ...m, form: { ...m.form, email: v } }))} placeholder="jane@example.com" />
                <TField label="Phone" testid="contact-phone" value={modal.form.phone} onChange={(v) => setModal((m) => ({ ...m, form: { ...m.form, phone: v } }))} placeholder="07…" />
              </div>
              <AddressAutocomplete label="Address" testid="contact-address" value={modal.form.address}
                onChange={(v) => setModal((m) => ({ ...m, form: { ...m.form, address: v } }))}
                onSelect={({ address, city, postcode }) => setModal((m) => ({ ...m, form: { ...m.form, address: [address, city, postcode].filter(Boolean).join(", ") } }))} />
              <TField label="Company / companies" testid="contact-company" value={modal.form.company} onChange={(v) => setModal((m) => ({ ...m, form: { ...m.form, company: v } }))} placeholder="e.g. Smith Holdings Ltd" />
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
                <textarea data-testid="contact-notes" rows={3} value={modal.form.notes} onChange={(e) => setModal((m) => ({ ...m, form: { ...m.form, notes: e.target.value } }))}
                  className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" placeholder="Any relevant notes…" />
              </div>
              <button data-testid="save-contact-button" type="submit" disabled={saving}
                className="w-full bg-blue-700 text-white hover:bg-blue-800 disabled:opacity-50 rounded-lg px-4 py-2.5 font-medium transition-colors flex items-center justify-center gap-2">
                {saving && <Loader2 size={16} className="animate-spin" />} {modal.edit ? "Save changes" : "Add contact"}
              </button>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}

function TField({ label, value, onChange, type = "text", placeholder, testid }) {
  return (
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-1">{label}</label>
      <input data-testid={testid} type={type} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-600" />
    </div>
  );
}
